import {FaceCluster} from '../../../shared/components/faceCluster';
import {Stamp, StickerCard} from '../../../shared/sticker/stickers';
import {type PreviewOption, type PreviewRow, previewModel} from '../resultsPreview';
import type {ResultsValue} from './resultsSettings';

const HEAD_TONES = 8;
const widthClass = (fill: number): string => `bt-w${Math.round(fill * 20) * 5}`;

const Heads = ({count}: {count: number}) => (
    <span className="bt-heads">
        {Array.from({length: count}, (_unused, n) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length run of identical dots
            <span key={n} className="bt-head" data-tone={n % HEAD_TONES} />
        ))}
    </span>
);

// Rows pass a fixed slot width so every name and bar starts at the same x.
const ROW_FACE_SLOT = 44;

const RowFace = ({row, size, maxWidth}: {row: PreviewRow; size: number; maxWidth?: number}) => (
    <FaceCluster
        title={row.name}
        photos={row.photos}
        names={row.names}
        size={size}
        maxWidth={maxWidth}
    />
);

const Row = ({row}: {row: PreviewRow}) => (
    <div className="bt-pv__row">
        <span className="bt-pv__pos">{row.place}</span>
        <span className="bt-pv__face">
            <RowFace row={row} size={20} maxWidth={ROW_FACE_SLOT} />
        </span>
        <span className="bt-pv__name" title={row.name}>
            {row.name}
        </span>
        {row.bar === 'heads' && <Heads count={row.heads} />}
        {row.bar === 'fill' && (
            <span className="bt-pv__bar">
                <i className={widthClass(row.fill)} />
            </span>
        )}
        {row.bar === 'none' && <span className="bt-pv__gap" />}
        <span className="bt-pv__num">{row.stat}</span>
    </div>
);

export const ResultsPreviewCard = ({
    value,
    options,
}: {
    value: ResultsValue;
    options: PreviewOption[];
}) => {
    const {winner, rows} = previewModel(options, value);
    if (!winner) {
        return (
            <section className="bt-pv" aria-label="Results preview">
                <div className="bt-label">Preview: how the reveal ends</div>
                <div className="bt-help">Add at least two options to preview the reveal.</div>
            </section>
        );
    }
    const bare = value.scope === 'winner' && !winner.stat && winner.bar === 'none';
    return (
        <section className="bt-pv" aria-label="Results preview">
            <div className="bt-label">Preview: how the reveal ends</div>
            <StickerCard seed="preview-winner" tape={false} className="bt-spot">
                <span className="bt-spot__stamp">
                    <Stamp>WINNER</Stamp>
                </span>
                <span className="bt-pv__face--big">
                    <RowFace row={winner} size={44} />
                </span>
                <b>{winner.name}</b>
                {winner.stat && <div className="bt-spot__stat">{winner.stat}</div>}
                {winner.bar === 'heads' && <Heads count={winner.heads} />}
            </StickerCard>
            {rows.map((row) => (
                <Row key={row.place} row={row} />
            ))}
            <div className="bt-help">Example numbers</div>
            {bare && <div className="bt-help">Just the winner, no numbers.</div>}
        </section>
    );
};
